import { UseFormValues } from "@formiz/core/dist/types/form.types";
import { AvatarPromptFormat, IAvatarPromptStyle, IDisplayableAvatarImageModel } from "@next-platform/packages-ts-types";
import { ChangeEvent, type HTMLAttributes, RefObject } from "react";

import type { GeneratedImage } from "@/types";
import { FormTypes } from "@/types";

export enum AvatarGeneratedImageType {
  GENERATION = "GENERATION",
  EDIT = "EDIT",
}

export type AvatarGeneratedImage = GeneratedImage & {
  id: string;
  generationType: AvatarGeneratedImageType;
  parentImageId?: string;
  editInstruction?: string;
  parentPrompt?: string;
  isNsfw?: boolean;
};

// Known tag strings the UI renders as badges. Values come from IAvatarImageModel.tag (open-ended string),
// but only these three have translated badge styling. Unknown tag values render as the raw string.
export enum ModelTag {
  NEW = "NEW",
  POPULAR = "POPULAR",
  BETA = "BETA",
}

export interface IAvatarGeneratorModalProps {
  isVisible: boolean;
  currentAvatarUrl: string;
  onClose: () => void;
  onSave: (key: string) => void;
  form?: UseFormValues;
  formType?: FormTypes;
  isAiPromptEnabled?: boolean;
}

// Component Props
export interface IImageGalleryProps {
  generatedImages: AvatarGeneratedImage[];
  currentImageIndex: number;
  isAvatarGenerating: boolean;
  isDragging: boolean;
  containerProps: HTMLAttributes<HTMLDivElement> & { ref: RefObject<HTMLDivElement | null> };
  prompt: string;
  modelStyle: string;
  onNavigatePrev: () => void;
  onNavigateNext: () => void;
  onImageSelect: (index: number) => void;
  onImageEdit?: (index: number) => void;
  onImageCrop?: (index: number, croppedBase64: string) => void;
  onImageDelete?: (index: number) => void;
}

export interface IPromptSectionProps {
  prompt: string;
  isAiPromptEnabled: boolean;
  isRequiredFieldsFilled: boolean;
  activeGeneratingField: string | null;
  suggestionField: string;
  promptFormat: AvatarPromptFormat;
  onPromptChange: (value: string) => void;
  onGenerateSuggestField: () => Promise<void>;
  errorMessage?: string;
}

export interface IModelSelectorProps {
  imageModels: IDisplayableAvatarImageModel[];
  modelStyle: string;
  onModelChange: (modelId: string) => void;
  /** Invoked when the user clicks a visible-but-locked model (permission not satisfied). */
  onDisabledModelClick?: () => void;
}

export interface IPromptImageStyleSelectorProps {
  promptImageStyle: string;
  promptImageStyles: IAvatarPromptStyle[];
  onStyleChange: (styleId: string) => void;
}

export interface IAdvancedOptionsProps {
  supportsNegativePrompt: boolean;
  supportsReferenceImage: boolean;
  negativePrompt: string;
  guidanceImage: string | ArrayBuffer | null;
  fixedSeed: string;
  seedLocked: boolean;
  generatedImagesCount: number;
  fileInputRef: RefObject<HTMLInputElement | null>;
  onNegativePromptChange: (value: string) => void;
  onRemoveGuidanceImage: () => void;
  onImageUploadClick: () => void;
  onImageUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  onSeedLockToggle: () => void;
}
